/**
 * Define all UI URL paths here and give each a unique name.
 * The names are used to reference the paths in the application where navigation is initiated.
 */
const uiPathsWithNames = {
  // Dashboard
  '/dashboard': 'dashboard',
  '/dashboard/dashboard-settings': 'dashboardSettings',
  // Data Dictionary
  '/data-dictionary': 'dataDictionary',
  // Classifiers
  '/data-dictionary/classifiers': 'classifierDirectory',
  '/data-dictionary/classifiers/:id': 'classifierView',
  '/data-dictionary/classifiers/:id/edit': 'classifierEdit',
  '/data-dictionary/classifiers/:id/clone': 'classifierClone',
  '/data-dictionary/classifiers/create': 'classifierCreate',
  // Classifier Mappings
  '/data-dictionary/classifier-mappings': 'classifierMappingDirectory',
  '/data-dictionary/classifier-mappings/:id': 'classifierMappingView',
  '/data-dictionary/classifier-mappings/:id/edit': 'classifierMappingEdit',
  '/data-dictionary/classifier-mappings/:id/clone': 'classifierMappingClone',
  '/data-dictionary/classifier-mappings/create': 'classifierMappingCreate',
  // Input Data Object Descriptions
  '/data-dictionary/input-data-objects': 'inputDataObjectDirectory',
  '/data-dictionary/input-data-objects/:id': 'inputDataObjectView',
  '/data-dictionary/input-data-objects/:id/edit': 'inputDataObjectEdit',
  '/data-dictionary/input-data-objects/:id/clone': 'inputDataObjectClone',
  '/data-dictionary/input-data-objects/create': 'inputDataObjectCreate',
  // Output Data Object Descriptions
  '/data-dictionary/output-data-objects': 'outputDataObjectDirectory',
  '/data-dictionary/output-data-objects/:id': 'outputDataObjectView',
  '/data-dictionary/output-data-objects/:id/edit': 'outputDataObjectEdit',
  '/data-dictionary/output-data-objects/:id/clone': 'outputDataObjectClone',
  '/data-dictionary/output-data-objects/create': 'outputDataObjectCreate',
  // Data
  '/data': 'data',
  // Input Data
  '/data/input-data': 'inputDataDirectory',
  '/data/input-data/errors': 'inputDataErrorDirectory',
  '/data/input-data/errors/:eventId': 'inputDataErrorReport',
  '/data/input-data/:id': 'inputDataView',
  '/data/input-data/details': 'inputDataDetails',
  // Output Data
  '/data/output-data': 'outputDataDirectory',
  '/data/output-data/errors': 'outputDataErrorDirectory',
  '/data/output-data/errors/:type/:id': 'outputDataErrorReport',
  '/data/output-data/errors/:type/:id/details/:calculationErrorId':
    'outputDataCalculationErrorReportDetails',
  '/data/output-data/errors/:type/:id/details/:validationErrorId':
    'outputDataValidationErrorReportDetails',
  '/data/output-data/test-calculation': 'outputDataTestCalculationReport',
  '/data/output-data/test-validation': 'outputDataTestValidationReport',
  // Data Subjects
  '/data/data-subjects': 'dataSubjectDirectory',
  '/data/data-subjects/:id': 'dataSubjectView',
  '/data/data-subjects/:id/edit': 'dataSubjectEdit',
  '/data/data-subjects/create': 'dataSubjectCreate',
  // Samples
  '/data/samples': 'sampleDirectory',
  '/data/samples/:id': 'sampleView',
  // Users
  '/users': 'userDirectory',
  '/users/:id': 'userView',
  '/users/:id/edit': 'userEdit',
  '/users/create': 'userCreate',
  // Settings
  '/settings': 'settings',
  '/settings/access-groups': 'accessGroupDirectory',
  '/settings/access-groups/:id': 'accessGroupDetails',
  '/settings/access-groups/:id/edit': 'accessGroupEdit',
  '/settings/access-groups/create': 'accessGroupCreate',
  '/settings/system-classifiers': 'systemClassifierDirectory',
  '/settings/system-classifiers/:id': 'systemClassifierDetails',
  '/settings/system-classifiers/:id/edit': 'systemClassifierEdit',
  '/settings/data-object-groups': 'dataObjectGroupDirectory',
  '/settings/data-object-groups/:id': 'dataObjectGroupDetails',
  '/settings/data-object-groups/:id/edit': 'dataObjectGroupEdit',
  '/settings/data-object-groups/create': 'dataObjectGroupCreate',
  '/settings/roles': 'roleDirectory',
  '/settings/roles/:id': 'roleDetails',
  '/settings/roles/:id/edit': 'roleEdit',
  '/settings/roles/create': 'roleCreate',
  '/settings/roles/:id/clone': 'roleClone',
  '/settings/test-screen': 'testScreen',
  // Tokens
  '/tokens': 'tokens',
  // Business Log
  '/business-log': 'businessLog',
  // Relations
  '/relations': 'relations',
  // Visual Validation
  '/visual-validation': 'visualValidation',
  '/visual-validation/generating': 'visualValidationGenerating',
  '/visual-validation/cross-table': 'visualValidationCrossTable',
  '/visual-validation/time-series': 'visualValidationTimeSeries',
  '/visual-validation/comparison': 'visualValidationComparison',
  '/visual-validation/time-series-comparison':
    'visualValidationTimeSeriesComparison',
  '/visual-validation/report': 'visualValidationReport',
  '/visual-validation/report/:dataObjectVersionId/indicator/:indicatorCode':
    'visualValidationReportIndicatorValue',
} as const;

export type UiPathsWithNames = typeof uiPathsWithNames;
export type UiPath = keyof UiPathsWithNames;

export type UiPathsByName = {
  [K in keyof UiPathsWithNames as UiPathsWithNames[K]]: K;
};

/**
 * Utility type for all or specific UI path names.
 *
 * @example
 * // resolves to a union type with all names
 * UiPathName
 * @example
 * // resolves to a union type with given specific names: 'dashboard' | 'userDirectory'
 * UiPathName<'dashboard' | 'userDirectory'>
 * @example
 * // Produces TS2344: Type 'foo' does not satisfy the constraint
 * UiPathName<'foo'>
 */
export type UiPathName<
  T extends Partial<keyof UiPathsByName> = keyof UiPathsByName,
> = T;

/**
 * Maps UI path names to their paths, type-safely, supporting autocompletion.
 */
export const path: UiPathsByName = Object.entries(uiPathsWithNames).reduce(
  (flipped, [key, value]) => ({ ...flipped, [value]: key }),
  {} as UiPathsByName,
);

// Ensure that all given UI path names are unique.
const uiPathNames = Object.values(uiPathsWithNames);
if (uiPathNames.length > new Set(uiPathNames).size) {
  throw Error('Duplicate UI path names found.');
}
